import { Heading3 } from '@casavo/base-ui'
import useTranslation from 'next-translate/useTranslation'
import getConfig from 'next/config'
import Image from 'next/image'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'

import { SearchInput } from '@/components/_base/SearchInput'
import { useCities } from '@/context/Cities'
import { ModalContextType } from '@/context/ModalContext'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { CanonicalFilter, FilterZone, GeoFilter } from '@/types/filter'
import { GTMEventCategory } from '@/utils/tracking/eventCategory'
import { handleFormFieldSet, handleListingsFilterArea } from '@/utils/tracking/eventsHandlers'

import { ModalBase } from '../../ModalBase'

import { applyGeoFilters } from './apply-geo-filters'
import Body from './Body'
import Footer from './Footer'

type Props = {
  canonicalFilter: CanonicalFilter
  closeModal: ModalContextType['closeModal']
  initialFilters: GeoFilter[]
  listingCountByZone: FilterZone[]
}

export type CityCount = {
  label: string
  slug: string
}

export const GeoFilterModal: React.FC<Props> = ({ initialFilters = [], canonicalFilter, listingCountByZone }) => {
  const cities = useCities()
  const [activeFilters, setActiveFilters] = useState<GeoFilter[]>(initialFilters)
  const [searchString, setSearchString] = useState('')
  const { t } = useTranslation()
  const { lang } = useTranslation()
  const router = useRouter()

  const {
    publicRuntimeConfig: { casavoUiCdn },
  } = getConfig()

  const applyFilters = () => {
    handleListingsFilterArea('Submit', 'SearchArea')
    applyGeoFilters(canonicalFilter, cities, activeFilters, lang, router)
  }

  return (
    <ModalBase handleClose={() => handleListingsFilterArea('Click', 'Close')}>
      <header className={sprinkles({ flexShrink: 0 })}>
        <div className={sprinkles({ alignItems: 'center', display: 'flex', justifyContent: 'space-between' })}>
          <div
            className={sprinkles({
              alignItems: { desktop: 'flex-end', mobile: 'center' },
              display: 'flex',
              gap: 's-m',
            })}
          >
            <Image
              alt=""
              height={36}
              src={`${casavoUiCdn}/illustrations/cities/${
                cities.router.area.country
              }/${cities.router.area.name.toLowerCase()}-small.svg`}
              width={36}
            />

            <Heading3>{t('common:where-looking-for')}</Heading3>
          </div>
        </div>
        <div className={sprinkles({ marginBottom: 'l', marginTop: 'l' })}>
          <SearchInput
            dataTestId="geofilters-search-input"
            placeholder={t('zones:search-filters')}
            value={searchString}
            onBlur={(value) => handleFormFieldSet('ListingsFilterArea', 'SearchBox', value)}
            onChange={setSearchString}
          />
        </div>
        <hr />
      </header>

      <Body
        activeFilters={activeFilters}
        eventCategory={GTMEventCategory.GeoFilterModalInCityPage}
        listingCountByZone={listingCountByZone}
        search={searchString}
        setActiveFilters={setActiveFilters}
        setSearch={setSearchString}
      />

      <Footer activeFilters={activeFilters} onSubmit={applyFilters} />
    </ModalBase>
  )
}
